import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AdminUpcoming = () => {
  const [upcomingEpisodes, setUpcomingEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialog, setDialog] = useState({ isOpen: false, type: 'alert', title: '', message: '', onConfirm: null });
  const navigate = useNavigate();

  const showAlert = (title, message, onConfirm = null) => {
    setDialog({ isOpen: true, type: 'alert', title, message, onConfirm });
  };

  const showConfirm = (title, message, onConfirm) => {
    setDialog({ isOpen: true, type: 'confirm', title, message, onConfirm });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    fetchUpcoming();
  }, [currentPage, searchTerm]);

  const fetchUpcoming = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: currentPage.toString(),
        size: '10'
      });

      if (searchTerm) params.append('search', searchTerm);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upcoming/?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch upcoming episodes');

      const data = await response.json();
      setUpcomingEpisodes(data.items);
      setTotalPages(data.pages);
    } catch (error) {
      console.error('Error fetching upcoming episodes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (episode) => {
    navigate(`/admin/upcoming/edit/${episode.id}`);
  };

  const executeDelete = async (episodeId) => {
    try {
      const token = localStorage.getItem('adminToken');
      
      if (!token) {
        showAlert('Authentication Required', 'Please log in again.', () => navigate('/admin/login'));
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upcoming/${episodeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('adminToken');
        showAlert('Session Expired', 'Your session has expired. Please log in again.', () => navigate('/admin/login'));
        return;
      }

      if (!response.ok) throw new Error('Failed to delete upcoming episode');

      fetchUpcoming();
      showAlert('Deleted', 'Upcoming episode has been successfully deleted.');
    } catch (error) {
      console.error('Error deleting upcoming episode:', error);
      showAlert('Error', 'Failed to delete upcoming episode');
    }
  };

  const handleDelete = (episodeId) => {
    showConfirm(
      'Delete Upcoming Episode',
      'Are you sure you want to completely delete this upcoming episode? This action cannot be undone.',
      () => executeDelete(episodeId)
    );
  };

  const handleToggleActive = async (episodeId) => {
    try {
      const token = localStorage.getItem('adminToken');
      
      if (!token) {
        showAlert('Authentication Required', 'Please log in again.', () => navigate('/admin/login'));
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upcoming/${episodeId}/toggle-active`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        alert('Your session has expired. Please log in again.');
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) throw new Error('Failed to toggle episode status');

      fetchUpcoming();
    } catch (error) {
      console.error('Error toggling episode status:', error);
      showAlert('Error', 'Failed to toggle episode status');
    }
  };



  // Toggle Switch Component
  const ToggleSwitch = ({ isActive, onToggle, episodeName }) => (
    <div className="flex items-center gap-3">
      <button
        onClick={onToggle}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 ${
          isActive ? 'bg-green-500' : 'bg-gray-600'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
            isActive ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
      <span className={`text-sm font-medium ${isActive ? 'text-green-400' : 'text-gray-400'}`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    </div>
  );

  const UpcomingCard = ({ episode }) => {
    return (
      <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-6 hover:scale-[1.02] transition-transform duration-300">
        <div className="flex items-start gap-4">
          {/* Thumbnail Image */}
          <div className="w-16 h-16 rounded-xl border border-slate-dark/20 p-2 flex items-center justify-center bg-transparent flex-shrink-0">
            <img
              src={episode.thumbnail_url || episode.thumbnailUrl}
              alt={episode.title}
              className="w-full h-full object-contain"
            />
          </div>
          
          <div className="flex-1 text-left">
            <div className="mb-2">
              <h3 className="font-bold text-warm-white text-xl text-left">{episode.title}</h3>
            </div>
            <p className="text-soft-gray text-sm mb-2 text-left">Guest: {episode.guest_name || episode.guestName}</p>
            <blockquote className="text-electric-yellow italic text-sm mb-4 text-left">
              "{episode.quote}"
            </blockquote>
          </div>
        </div>
        <div className="flex items-center justify-between pt-4 border-t border-slate-dark/30">
          {/* Toggle Switch on the left */}
          <ToggleSwitch 
            isActive={episode.is_active ?? episode.isActive}
            onToggle={() => handleToggleActive(episode.id)}
            episodeName={episode.title}
          />
          
          {/* Edit and Delete buttons on the right */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleEdit(episode)}
              className="bg-electric-yellow hover:bg-electric-yellow/90 text-midnight px-4 py-2 rounded-lg transition-colors duration-300 text-left font-bold"
            >
              Edit
            </button>
            <button
              onClick={() => handleDelete(episode.id)}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors duration-300 text-left"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-electric-yellow text-lg">Loading upcoming episodes...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-clash text-3xl font-bold text-electric-yellow">Upcoming Episodes</h2>
        <button
          onClick={() => navigate('/admin/upcoming/create')}
          className="bg-electric-yellow text-midnight px-6 py-3 rounded-xl font-bold hover:bg-electric-yellow/90 transition-colors duration-300"
        >
          + Add Upcoming Episode
        </button>
      </div>



      {/* Search */}
      <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-6">
        <input
          type="text"
          placeholder="Search upcoming episodes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50"
        />
      </div>

      {/* Upcoming Episodes List */}
      <div className="space-y-4">
        {upcomingEpisodes.length === 0 ? (
          <div className="text-center py-12 text-soft-gray">
            No upcoming episodes found. Create your first upcoming episode!
          </div>
        ) : (
          upcomingEpisodes.map((episode) => (
            <UpcomingCard key={episode.id} episode={episode} />
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 pt-6">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="bg-charcoal/40 text-warm-white px-4 py-2 rounded-lg disabled:opacity-50 hover:bg-electric-yellow/10 transition-colors duration-300"
          >
            Previous
          </button>
          <span className="text-soft-gray">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="bg-charcoal/40 text-warm-white px-4 py-2 rounded-lg disabled:opacity-50 hover:bg-electric-yellow/10 transition-colors duration-300"
          >
            Next
          </button>
        </div>
      )}

      {/* Custom Dialog Modal */}
      {dialog.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-midnight/80 backdrop-blur-sm">
          <div className="bg-slate-dark border border-electric-yellow/30 rounded-2xl p-6 max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-200">
            <h3 className="text-xl font-bold text-electric-yellow mb-2">{dialog.title}</h3>
            <p className="text-soft-gray mb-6">{dialog.message}</p>
            <div className="flex justify-end gap-3">
              {dialog.type === 'confirm' && (
                <button
                  onClick={closeDialog}
                  className="px-4 py-2 rounded-lg font-medium text-soft-gray hover:text-white hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={() => {
                  if (dialog.onConfirm) dialog.onConfirm();
                  closeDialog();
                }}
                className={`px-4 py-2 rounded-lg font-bold transition-transform hover:scale-105 ${
                  dialog.type === 'confirm' 
                    ? 'bg-red-500 text-white shadow-lg shadow-red-500/20' 
                    : 'bg-electric-yellow text-midnight shadow-lg shadow-electric-yellow/20'
                }`}
              >
                {dialog.type === 'confirm' ? 'Delete' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUpcoming;